import type { ProductCondition } from "@/features/marketplace/types/product";

export interface ListingSpec {
  label: string;
  value: string;
}

/**
 * One draft, shared across the 3 wizard steps (`/sell/media`, `/sell/details`,
 * `/sell/finalize`) via `SellWizardContext` — each step's form reads/writes
 * its own slice, but nothing is submitted until `/sell/finalize` combines
 * all three into one request. Numeric fields stay strings here (native
 * input values) rather than numbers; converting happens once, at submit
 * time (`toListingFormData`), so no step has to fight a number/string
 * mismatch between an empty input and a parsed default.
 */
export interface ListingDraft {
  images: File[];
  title: string;
  description: string;
  categorySlug: string;
  condition: ProductCondition | "";
  brand: string;
  price: string;
  originalPrice: string;
  negotiable: boolean;
  city: string;
  state: string;
  tags: string;
  specs: ListingSpec[];
}

export const EMPTY_LISTING_DRAFT: ListingDraft = {
  images: [],
  title: "",
  description: "",
  categorySlug: "",
  condition: "",
  brand: "",
  price: "",
  originalPrice: "",
  negotiable: false,
  city: "",
  state: "",
  tags: "",
  specs: [],
};

/**
 * Built once, at the end of `/sell/finalize` — every field the backend's
 * `POST /products` (multipart) expects, matching `docs/database/products.md`'s
 * schema plus the `specs` extension `product.service.ts` (server) already
 * reads.
 */
export function toListingFormData(draft: ListingDraft, status: "active" | "draft"): FormData {
  const formData = new FormData();
  draft.images.forEach((file) => formData.append("images", file));
  formData.append("title", draft.title);
  formData.append("description", draft.description);
  formData.append("category", draft.categorySlug);
  formData.append("condition", draft.condition);
  if (draft.brand) formData.append("brand", draft.brand);
  formData.append("price", draft.price);
  if (draft.originalPrice) formData.append("originalPrice", draft.originalPrice);
  formData.append("negotiable", String(draft.negotiable));
  formData.append("city", draft.city);
  formData.append("state", draft.state);
  formData.append("status", status);
  const tags = draft.tags
    .split(",")
    .map((tag) => tag.trim())
    .filter(Boolean);
  if (tags.length > 0) formData.append("tags", JSON.stringify(tags));
  if (draft.specs.length > 0) formData.append("specs", JSON.stringify(draft.specs));
  return formData;
}
